import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { kr } from '../lib/format';
import { PageHeader, Card, StatCard, Modal } from '../components/ui';
import { Clock, Plus, FileText, Trash2, Edit3, CheckCircle } from 'lucide-react';

const idag = () => new Date().toISOString().slice(0, 10);
const startMnd = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

const Timer = () => {
  const [rader, setRader] = useState([]);
  const [prosjekter, setProsjekter] = useState([]);
  const [kunder, setKunder] = useState([]);
  const [filter, setFilter] = useState({ fra: startMnd(), til: idag(), prosjekt_id: '', kunde_id: '', fakturert: 'alle' });
  const [skjema, setSkjema] = useState(null);
  const [valgt, setValgt] = useState({});
  const [fakturaModal, setFakturaModal] = useState(null);

  const last = useCallback(() => {
    const params = {};
    if (filter.fra) params.fra = filter.fra;
    if (filter.til) params.til = filter.til;
    if (filter.prosjekt_id) params.prosjekt_id = filter.prosjekt_id;
    if (filter.kunde_id) params.kunde_id = filter.kunde_id;
    if (filter.fakturert !== 'alle') params.fakturert = filter.fakturert;
    api.get('/timer', { params }).then((r) => setRader(r.data));
  }, [filter]);

  useEffect(() => { last(); }, [last]);
  useEffect(() => {
    api.get('/prosjekt').then((r) => setProsjekter(r.data)).catch(() => {});
    api.get('/kontakter').then((r) => setKunder((r.data || []).filter(k => k.type !== 'leverandor'))).catch(() => {});
  }, []);

  const apneNy = () => setSkjema({
    dato: idag(), prosjekt_id: '', kunde_id: '', timer: '', timepris: '', beskrivelse: '', fakturerbar: true,
  });
  const apneRediger = (t) => setSkjema({
    id: t.id, dato: t.dato, prosjekt_id: t.prosjekt_id || '', kunde_id: t.kunde_id || '',
    timer: t.timer, timepris: t.timepris, beskrivelse: t.beskrivelse, fakturerbar: t.fakturerbar,
  });

  const lagre = async () => {
    if (!skjema.dato || !skjema.timer) { alert('Dato og timer kreves'); return; }
    const body = {
      dato: skjema.dato,
      prosjekt_id: skjema.prosjekt_id || null,
      kunde_id: skjema.kunde_id || null,
      timer: parseFloat(skjema.timer) || 0,
      timepris: parseFloat(skjema.timepris) || 0,
      beskrivelse: skjema.beskrivelse || '',
      fakturerbar: !!skjema.fakturerbar,
    };
    if (skjema.id) await api.patch(`/timer/${skjema.id}`, body);
    else await api.post('/timer', body);
    setSkjema(null); last();
  };

  const slett = async (t) => {
    if (!window.confirm('Slette denne timeføringen?')) return;
    try { await api.delete(`/timer/${t.id}`); last(); } catch (e) { alert(e.response?.data?.error || 'Feil'); }
  };

  const sumTimer = rader.reduce((s, t) => s + (t.timer || 0), 0);
  const sumKr = rader.reduce((s, t) => s + (t.sum || 0), 0);
  const sumUfak = rader.filter(t => !t.fakturert && t.fakturerbar).reduce((s, t) => s + (t.sum || 0), 0);

  const valgteIds = Object.entries(valgt).filter(([, v]) => v).map(([k]) => Number(k));
  const valgteRader = rader.filter(r => valgteIds.includes(r.id));
  const valgtKunde = valgteRader.length && valgteRader.every(r => r.kunde_id === valgteRader[0].kunde_id) ? valgteRader[0].kunde_id : null;

  const lagFaktura = async () => {
    if (!fakturaModal?.kunde_id) { alert('Velg kunde'); return; }
    try {
      const r = await api.post('/timer/til-faktura', {
        kunde_id: fakturaModal.kunde_id,
        timer_ids: fakturaModal.timer_ids || null,
        dato: fakturaModal.dato || idag(),
        forfall: fakturaModal.forfall || idag(),
        notat: fakturaModal.notat || '',
      });
      alert(`Faktura ${r.data.fakturanummer} opprettet (${r.data.antall_timer} timer).`);
      setFakturaModal(null); setValgt({}); last();
    } catch (e) { alert(e.response?.data?.error || 'Feil'); }
  };

  return (
    <div>
      <PageHeader title="Timeføring" subtitle="Timer per ansatt og prosjekt" actions={
        <>
          <button className="btn-primary" onClick={apneNy}><Plus size={14} /> Ny timeføring</button>
          <button className="btn-ghost" disabled={!valgteIds.length || !valgtKunde}
                  onClick={() => setFakturaModal({ kunde_id: valgtKunde, timer_ids: valgteIds, dato: idag(), forfall: idag() })}>
            <FileText size={14} /> Fakturer valgte ({valgteIds.length})
          </button>
        </>
      } />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <StatCard label="Sum timer" value={sumTimer.toFixed(1)} color="var(--theme-accent)" icon={Clock} />
        <StatCard label="Sum kr" value={kr(sumKr)} color="var(--theme-text)" />
        <StatCard label="Ufakturert" value={kr(sumUfak)} color="var(--theme-warning)" />
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'end' }}>
          <div><label className="fm-text-muted" style={{ fontSize: 11 }}>Fra</label>
            <input type="date" className="fm-input" value={filter.fra} onChange={(e) => setFilter({ ...filter, fra: e.target.value })} /></div>
          <div><label className="fm-text-muted" style={{ fontSize: 11 }}>Til</label>
            <input type="date" className="fm-input" value={filter.til} onChange={(e) => setFilter({ ...filter, til: e.target.value })} /></div>
          <div><label className="fm-text-muted" style={{ fontSize: 11 }}>Prosjekt</label>
            <select className="fm-input" value={filter.prosjekt_id} onChange={(e) => setFilter({ ...filter, prosjekt_id: e.target.value })}>
              <option value="">Alle</option>
              {prosjekter.map(p => <option key={p.id} value={p.id}>{p.navn}</option>)}
            </select></div>
          <div><label className="fm-text-muted" style={{ fontSize: 11 }}>Kunde</label>
            <select className="fm-input" value={filter.kunde_id} onChange={(e) => setFilter({ ...filter, kunde_id: e.target.value })}>
              <option value="">Alle</option>
              {kunder.map(k => <option key={k.id} value={k.id}>{k.navn}</option>)}
            </select></div>
          <div><label className="fm-text-muted" style={{ fontSize: 11 }}>Fakturert</label>
            <select className="fm-input" value={filter.fakturert} onChange={(e) => setFilter({ ...filter, fakturert: e.target.value })}>
              <option value="alle">Alle</option><option value="nei">Ikke fakturert</option><option value="ja">Fakturert</option>
            </select></div>
        </div>

        <table className="fm-table">
          <thead><tr>
            <th></th><th>Dato</th><th>Ansatt</th><th>Prosjekt</th><th>Kunde</th><th>Beskrivelse</th>
            <th style={{ textAlign: 'right' }}>Timer</th><th style={{ textAlign: 'right' }}>Pris</th><th style={{ textAlign: 'right' }}>Sum</th>
            <th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {rader.map(t => (
              <tr key={t.id}>
                <td><input type="checkbox" disabled={t.fakturert || !t.fakturerbar}
                  checked={!!valgt[t.id]} onChange={(e) => setValgt({ ...valgt, [t.id]: e.target.checked })} /></td>
                <td>{t.dato}</td>
                <td>{t.ansatt_navn}</td>
                <td>{t.prosjekt_navn}</td>
                <td>{t.kunde_navn}</td>
                <td>{t.beskrivelse}</td>
                <td style={{ textAlign: 'right' }}>{t.timer.toFixed(1)}</td>
                <td style={{ textAlign: 'right' }}>{kr(t.timepris)}</td>
                <td style={{ textAlign: 'right' }}>{kr(t.sum)}</td>
                <td>{t.fakturert
                  ? <span style={{ color: 'var(--theme-success)' }}><CheckCircle size={12} /> Fakturert</span>
                  : (t.fakturerbar ? 'Klar' : 'Ikke fakturerbar')}</td>
                <td>
                  {!t.fakturert && <>
                    <button className="btn-ghost" onClick={() => apneRediger(t)} title="Rediger"><Edit3 size={13} /></button>
                    <button className="btn-ghost" onClick={() => slett(t)} title="Slett" style={{ color: 'var(--theme-danger)' }}><Trash2 size={13} /></button>
                  </>}
                </td>
              </tr>
            ))}
            {!rader.length && <tr><td colSpan={11} className="fm-text-muted" style={{ textAlign: 'center', padding: 24 }}>Ingen timeføringer.</td></tr>}
          </tbody>
        </table>
      </Card>

      {skjema && (
        <Modal open onClose={() => setSkjema(null)} title={skjema.id ? 'Rediger timeføring' : 'Ny timeføring'}
               footer={<><button className="btn-ghost" onClick={() => setSkjema(null)}>Avbryt</button>
                         <button className="btn-primary" onClick={lagre}>Lagre</button></>}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div><label>Dato</label><input type="date" className="fm-input" value={skjema.dato} onChange={(e) => setSkjema({ ...skjema, dato: e.target.value })} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label>Timer</label><input type="number" step="0.25" className="fm-input" value={skjema.timer} onChange={(e) => setSkjema({ ...skjema, timer: e.target.value })} /></div>
              <div><label>Timepris</label><input type="number" step="1" className="fm-input" value={skjema.timepris} onChange={(e) => setSkjema({ ...skjema, timepris: e.target.value })} /></div>
            </div>
            <div><label>Prosjekt</label>
              <select className="fm-input" value={skjema.prosjekt_id} onChange={(e) => {
                const p = prosjekter.find(x => x.id === Number(e.target.value));
                setSkjema({ ...skjema, prosjekt_id: e.target.value, kunde_id: p?.kunde_id || skjema.kunde_id });
              }}>
                <option value="">– ingen –</option>
                {prosjekter.map(p => <option key={p.id} value={p.id}>{p.navn}</option>)}
              </select>
            </div>
            <div><label>Kunde</label>
              <select className="fm-input" value={skjema.kunde_id} onChange={(e) => setSkjema({ ...skjema, kunde_id: e.target.value })}>
                <option value="">– ingen –</option>
                {kunder.map(k => <option key={k.id} value={k.id}>{k.navn}</option>)}
              </select>
            </div>
            <div><label>Beskrivelse</label>
              <textarea className="fm-input" rows={3} value={skjema.beskrivelse} onChange={(e) => setSkjema({ ...skjema, beskrivelse: e.target.value })} /></div>
            <div><label><input type="checkbox" checked={!!skjema.fakturerbar} onChange={(e) => setSkjema({ ...skjema, fakturerbar: e.target.checked })} /> Fakturerbar</label></div>
          </div>
        </Modal>
      )}

      {fakturaModal && (
        <Modal open onClose={() => setFakturaModal(null)} title="Lag faktura fra timer"
               footer={<><button className="btn-ghost" onClick={() => setFakturaModal(null)}>Avbryt</button>
                         <button className="btn-primary" onClick={lagFaktura}>Opprett faktura</button></>}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div><label>Kunde</label>
              <select className="fm-input" value={fakturaModal.kunde_id || ''} onChange={(e) => setFakturaModal({ ...fakturaModal, kunde_id: e.target.value })}>
                <option value="">– velg –</option>
                {kunder.map(k => <option key={k.id} value={k.id}>{k.navn}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label>Fakturadato</label><input type="date" className="fm-input" value={fakturaModal.dato} onChange={(e) => setFakturaModal({ ...fakturaModal, dato: e.target.value })} /></div>
              <div><label>Forfall</label><input type="date" className="fm-input" value={fakturaModal.forfall} onChange={(e) => setFakturaModal({ ...fakturaModal, forfall: e.target.value })} /></div>
            </div>
            <div className="fm-text-muted" style={{ fontSize: 12 }}>
              {fakturaModal.timer_ids?.length
                ? `${fakturaModal.timer_ids.length} valgte timer blir fakturert.`
                : 'Alle ufakturerte timer for kunden blir fakturert.'}
            </div>
            <div><label>Notat</label><input className="fm-input" value={fakturaModal.notat || ''} onChange={(e) => setFakturaModal({ ...fakturaModal, notat: e.target.value })} /></div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Timer;
